import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
const BASE_URL = process.env.REACT_APP_BASE_URL;

const SelfAssessmentForm = () => {
  const [formData, setFormData] = useState({
    employeeID: "",
    workQuality: "",
    productivity: "",
    communicationSkills: "",
    teamwork: "",
    problemSolvingSkills: "",
    attendancePunctuality: "",
    achievements: "",
    skillsToImprove: "",
    supportNeeded: "",
    shortTermGoals: "",
    longTermGoals: "",
    employeeComments: "",
    overallRating: "",
    strengths: "",
    learningActivities: "",
    challengesFaced: "",
    selfImprovementEfforts: "",
    feedbackOnManagement: "",
    careerAspirations: "",
    preferredWorkStyle: "",
    peerFeedback: "",
    managerComments: "",
    developmentPlan: "",
    assessmentPeriod: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/feedback/selfassesmentform`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to submit self-assessment"
        );
      }

      alert("Self-assessment submitted successfully!");
      setFormData({
        employeeID: "",
        workQuality: "",
        productivity: "",
        communicationSkills: "",
        teamwork: "",
        problemSolvingSkills: "",
        attendancePunctuality: "",
        achievements: "",
        skillsToImprove: "",
        supportNeeded: "",
        shortTermGoals: "",
        longTermGoals: "",
        employeeComments: "",
        overallRating: "",
        strengths: "",
        learningActivities: "",
        challengesFaced: "",
        selfImprovementEfforts: "",
        feedbackOnManagement: "",
        careerAspirations: "",
        preferredWorkStyle: "",
        peerFeedback: "",
        managerComments: "",
        developmentPlan: "",
        assessmentPeriod: "",
      });
    } catch (err) {
      console.error("Error submitting self-assessment:", err);
      alert("Error submitting self-assessment. Please try again.");
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#8b8fa3" },
      "&.Mui-focused fieldset": { borderColor: "#667eea", borderWidth: 1.5 },
    },
  };

  const optionalFields = [
    "achievements",
    "skillsToImprove",
    "supportNeeded",
    "shortTermGoals",
    "longTermGoals",
    "employeeComments",
  ];

  const sections = [
    {
      title: "Employee Information",
      fields: ["employeeID", "assessmentPeriod"],
    },
    {
      title: "Performance Evaluation",
      fields: [
        "workQuality",
        "productivity",
        "communicationSkills",
        "teamwork",
        "problemSolvingSkills",
        "attendancePunctuality",
      ],
    },
    {
      title: "Achievements & Development",
      fields: [
        "achievements",
        "skillsToImprove",
        "strengths",
        "learningActivities",
      ],
    },
    {
      title: "Goals & Aspirations",
      fields: [
        "shortTermGoals",
        "longTermGoals",
        "careerAspirations",
        "supportNeeded",
      ],
    },
    {
      title: "Challenges & Growth",
      fields: [
        "challengesFaced",
        "selfImprovementEfforts",
        "preferredWorkStyle",
      ],
    },
    {
      title: "Feedback & Comments",
      fields: [
        "feedbackOnManagement",
        "peerFeedback",
        "employeeComments",
        "managerComments",
        "developmentPlan",
      ],
    },
    {
      title: "Overall Rating",
      fields: ["overallRating"],
    },
  ];

  const formatLabel = (key) => {
    const label = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
    const isOptional = optionalFields.includes(key);
    return isOptional ? label : `${label} *`;
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Self Assessment"
        subtitle="Reflect on your performance and set future development goals"
        icon={<AssignmentIcon />}
      />

      <Paper
        elevation={0}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          p: 3,
        }}
      >
        {sections.map((section, sIdx) => (
          <Box key={sIdx} sx={{ mb: sIdx < sections.length - 1 ? 3 : 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#1a1a2e",
                pb: 1.5,
                mb: 2,
                borderBottom: "1px solid #f0f2f8",
              }}
            >
              {section.title}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: section.fields.length === 1 ? "1fr" : "1fr 1fr",
                },
                gap: 2,
              }}
            >
              {section.fields.map((key) => {
                const isTextarea = [
                  "employeeComments",
                  "managerComments",
                  "developmentPlan",
                  "feedbackOnManagement",
                  "peerFeedback",
                  "challengesFaced",
                  "selfImprovementEfforts",
                ].includes(key);
                return (
                  <Box
                    key={key}
                    sx={isTextarea ? { gridColumn: { sm: "1 / -1" } } : {}}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
                    >
                      {formatLabel(key)}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type={key === "overallRating" ? "number" : "text"}
                      name={key}
                      placeholder={`Enter ${key.replace(/([A-Z])/g, " $1").toLowerCase()}`}
                      value={formData[key]}
                      onChange={handleChange}
                      required={!optionalFields.includes(key)}
                      multiline={isTextarea}
                      minRows={isTextarea ? 3 : undefined}
                      sx={inputSx}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}

        {/* Submit */}
        <Box sx={{ pt: 2.5, mt: 1, borderTop: "1px solid #eef0f6" }}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 4,
              py: 1,
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
            }}
          >
            Submit Self-Assessment
          </Button>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default SelfAssessmentForm;
